import { history } from 'umi';
import { makeObservable, action, observable, runInAction } from 'mobx';
import { BaseService } from '../base';
import UserInfo from './api.interface';
import { UserInfoModel } from './userInfo.model';
import {
  RISK_REJECT_CODE,
  BIZ_TYPE,
  LOCK_TYPE,
  CodeType,
  UC_USER_STATUS,
} from './userInfo.interface';
import insight from '@/utils/v2/insight';
import { getLoginCode, goWsaUrl } from '@/utils/v2/authcode';
import { isShopeeSellerApp } from '@/utils/v2/device';
import { NO_PERMISSION } from '@/utils/v2/request';

const UNACTIVE = [30001, 30002, 30003, 90113, 90011]; // 黑名单用户错误码

export class UserInfoService extends BaseService {
  @observable userInfo: UserInfoModel = new UserInfoModel();

  constructor() {
    super();
    makeObservable(this);
  }

  isUnactivated(userInfo: UserInfoModel) {
    const userInfoState = userInfo;
    const { userStatus } = userInfoState;

    return userStatus < 20;
  }

  isFrozen(userInfo: UserInfoModel) {
    const { userStatus } = userInfo;
    return userStatus >= 30;
  }

  isContractUnsigned(userInfo: UserInfoModel) {
    const { userStatus } = userInfo;
    return userStatus === UC_USER_STATUS.CONTRACT_NOT_SIGNED;
  }

  /** / PRD：以 UC 返回的 user_status 判定冻结类型，不再依赖 lock_info_list 是否有值 */
  isCustomedTypeFrozen(userInfo: UserInfoModel, type: UC_USER_STATUS) {
    const { userStatus } = userInfo;
    return this.isFrozen(userInfo) && userStatus === type;
  }

  isDPDFrozen(userInfo: UserInfoModel) {
    const { userStatus } = userInfo;
    if (!this.isFrozen(userInfo)) {
      return false;
    }
    return (
      userStatus === UC_USER_STATUS.DPD_FROZEN_311 ||
      userStatus === UC_USER_STATUS.DPD_FROZEN_320 ||
      userStatus === UC_USER_STATUS.DPD_FROZEN_321
    );
  }

  isWhatTypeFrozen(userInfo: UserInfoModel, type: LOCK_TYPE) {
    const { lockInfoList } = userInfo;

    return (
      this.isFrozen(userInfo) &&
      lockInfoList?.some(
        (item) => item?.biz_type === BIZ_TYPE.FastEscrow && item?.lock_type === type,
      )
    );
  }

  // 贷中贷后只在存量用户提示updateInfo和路由控制用到UC拒绝码。
  isInActivationCoolTime(userInfo: UserInfoModel) {
    const userInfoState: UserInfoModel = userInfo;
    const { riskRejectCode, coolDownEndTime } = userInfoState;

    return !!(
      this.isUnactivated(userInfoState) &&
      [
        RISK_REJECT_CODE.FRAUD_COOL_DOWN1,
        RISK_REJECT_CODE.FRAUD_COOL_DOWN2,
        RISK_REJECT_CODE.UNDERWRITING,
        RISK_REJECT_CODE.VERIFICATION_COOL_DOWN,
      ].includes(riskRejectCode) &&
      coolDownEndTime
    );
  }

  isCDDWillExpire(userInfo: UserInfoModel) {
    const { userStatus, riskRejectCode } = userInfo;
    return (
      userStatus === UC_USER_STATUS.NORMAL && riskRejectCode === RISK_REJECT_CODE.KYC_WILL_EXPIRE
    );
  }

  isCDDExpired(userInfo: UserInfoModel) {
    const { userStatus, riskRejectCode } = userInfo;
    return (
      userStatus === UC_USER_STATUS.UPDATEINFO && riskRejectCode === RISK_REJECT_CODE.KYC_EXPIRED
    );
  }

  @action
  async getUserInfo() {
    const authCodeResp = await getLoginCode();

    if (!authCodeResp.token) {
      // errcode=0，表示是RN没取到，此时要跳转wsa url兜底；否则表示wsa也没取到，展示错误页就行
      authCodeResp.errorCode === 0 ? goWsaUrl() : history.replace({ pathname: '/exception' });
      return false;
    }

    const requestHeaderParams =
      authCodeResp.type === CodeType.AuthCode
        ? { 'X-Kredit-Req-AuthCode': authCodeResp.token }
        : { 'X-Kredit-Req-Nonce': authCodeResp.token };

    const { code: loginCode, result: loginResult } = await this.post(UserInfo.API.login, {
      headers: requestHeaderParams,
    });

    if (loginCode === NO_PERMISSION && isShopeeSellerApp()) {
      // 使用 replace 而不是 push，避免在历史记录中留下错误页面
      history.replace({ pathname: '/no-permission' });
      return false;
    }

    if (loginCode === 0) {
      const { code, result } = await this.post(UserInfo.API.get_user_info);
      if (code === 0) {
        const userInfo = {
          userId: loginResult?.user_id ?? '',
          lockInfoList: result.lock_info_list || [],
          userStatus: result.user_status,
          userTotalStatus: result.user_total_status,
          isStockUser: result.is_stock_user,
          screenStatus: result.screen_status,
          kycStatus: result.kyc_status,
          fraudStatus: result.fraud_status,
          overdueStatus: result.overdue_status,
          riskRejectCode: result.risk_reject_code,
          coolDownEndTime: result.cool_down_end_time,
          totalLimit: result.total_limit,
          availableLimit: result.available_limit,
          isFirstIn: result.is_first_in,
        } as UserInfoModel;

        localStorage.setItem('platform_user_id', result.platform_user_id);

        insight.loginReport({
          status: 'success',
          type: authCodeResp.type === CodeType.AuthCode ? 'auth_code' : 'nonce_code',
        });

        // UC DR Frozen
        if (this.isWhatTypeFrozen(userInfo, LOCK_TYPE.DR_DISABLE_USER)) {
          history.push({ pathname: '/exception', state: { status: 'drfrozen' } });
          return false;
        }

        runInAction(() => {
          this.userInfo = {
            ...userInfo,
            isUnactivated: this.isUnactivated(userInfo),
            isFrozen: this.isFrozen(userInfo),
            isKYCFrozenWithUpdateInfo: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.UPDATEINFO,
            ),
            isKYCFrozenWithUpdateInfoPending: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.FREEZE_UPDATE_INFO_PENDING,
            ),
            isKYCFrozenWithoutUpdateInfo: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.KYC_FROZEN,
            ),
            isFraudFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.FRAUD),
            isOverdueFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.OVERDUE),
            isSellerFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.SELLER_FROZEN),
            isCNPJFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CNPJ_FROZEN),
            isICFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.IC_FROZEN),
            isFSKYCFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.FS_KYC_FROZEN),
            isUnderFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.UNDER_WRITING_FREEZE),
            isPaymentRejectCoolDownFrozen: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.PAYMENT_REJECT_FROZEN,
            ),
            isTemporaryUnderwritingFrozen: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.TEMPORARY_UNDER_WRITING_FROZEN,
            ),
            isSPPFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.SPP_FROZEN),
            isSPPUnlinkFrozen: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.SPP_UNLINK_FROZEN,
            ),
            isContractFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CONTRACT_FROZEN),
            isCICFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CIC_FROZEN),
            isOtherCreditOverdueFrozen: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.OTHER_CREDIT_PRODUCT_OVERDUE_FROZEN,
            ),
            isDPDFrozen: this.isDPDFrozen(userInfo),
            isShopeeAccountFrozen: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.SHOPEE_STATUS_ACCOUNT_FROZEN,
            ),
            isContractUnsigned: this.isContractUnsigned(userInfo),
            isCDDWillExpire: this.isCDDWillExpire(userInfo),
            isCDDExpired: this.isCDDExpired(userInfo),
            islogin: true,
          };
        });

        return true;
      }
      insight.loginReport({
        status: 'error',
        type: authCodeResp.type === CodeType.AuthCode ? 'auth_code' : 'nonce_code',
      });

      console.error('调用获取用户信息接口报错');
      return false;
    }

    if (UNACTIVE.includes(loginCode as number)) {
      console.error('用户未被邀请');
      history.push({ pathname: '/exception', state: { status: 'uninvite' } });
      return false;
    }

    console.error('调用登录接口报错');
    return false;
  }

  @action
  async updateUserInfo() {
    const { code, result } = await this.post(UserInfo.API.get_user_info);
    if (code !== 0) {
      return false;
    }
    localStorage.setItem('platform_user_id', result.platform_user_id);

    const userInfo = {
      lockInfoList: result.lock_info_list || [],
      userStatus: result.user_status,
      userTotalStatus: result.user_total_status,
      isStockUser: result.is_stock_user,
      screenStatus: result.screen_status,
      kycStatus: result.kyc_status,
      fraudStatus: result.fraud_status,
      overdueStatus: result.overdue_status,
      riskRejectCode: result.risk_reject_code,
      coolDownEndTime: result.cool_down_end_time,
      totalLimit: result.total_limit,
      availableLimit: result.available_limit,
      isFirstIn: result.is_first_in,
    } as UserInfoModel;

    // UC DR Frozen
    if (this.isWhatTypeFrozen(userInfo, LOCK_TYPE.DR_DISABLE_USER)) {
      history.push({ pathname: '/exception', state: { status: 'drfrozen' } });
      // 这里返回true表示不需再请求后续接口了
      return true;
    }

    this.userInfo = {
      ...userInfo,
      isUnactivated: this.isUnactivated(userInfo),
      isFrozen: this.isFrozen(userInfo),
      isKYCFrozenWithUpdateInfo: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.UPDATEINFO),
      isKYCFrozenWithUpdateInfoPending: this.isCustomedTypeFrozen(
        userInfo,
        UC_USER_STATUS.FREEZE_UPDATE_INFO_PENDING,
      ),
      isKYCFrozenWithoutUpdateInfo: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.KYC_FROZEN),
      isFraudFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.FRAUD),
      isOverdueFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.OVERDUE),
      isSellerFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.SELLER_FROZEN),
      isCNPJFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CNPJ_FROZEN),
      isICFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.IC_FROZEN),
      isFSKYCFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.FS_KYC_FROZEN),
      isUnderFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.UNDER_WRITING_FREEZE),
      isPaymentRejectCoolDownFrozen: this.isCustomedTypeFrozen(
        userInfo,
        UC_USER_STATUS.PAYMENT_REJECT_FROZEN,
      ),
      isTemporaryUnderwritingFrozen: this.isCustomedTypeFrozen(
        userInfo,
        UC_USER_STATUS.TEMPORARY_UNDER_WRITING_FROZEN,
      ),
      isSPPFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.SPP_FROZEN),
      isSPPUnlinkFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.SPP_UNLINK_FROZEN),
      isContractFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CONTRACT_FROZEN),
      isCICFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CIC_FROZEN),
      isOtherCreditOverdueFrozen: this.isCustomedTypeFrozen(
        userInfo,
        UC_USER_STATUS.OTHER_CREDIT_PRODUCT_OVERDUE_FROZEN,
      ),
      isDPDFrozen: this.isDPDFrozen(userInfo),
      isShopeeAccountFrozen: this.isCustomedTypeFrozen(
        userInfo,
        UC_USER_STATUS.SHOPEE_STATUS_ACCOUNT_FROZEN,
      ),
      isContractUnsigned: this.isContractUnsigned(userInfo),
      isCDDWillExpire: this.isCDDWillExpire(userInfo),
      isCDDExpired: this.isCDDExpired(userInfo),
      islogin: true,
    };

    return true;
  }

  /**
   *
   *@description 上报insight
   */
  async requestInsight(requestData: Record<string, any>) {
    await this.pureRequest({
      url: UserInfo.API.insight_fe_chat,
      method: 'POST',
      requestData: requestData ?? {},
    });
  }
}
